import axios, { AxiosResponse } from 'axios';
import moment from 'moment';

const apiClient = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
  responseType: 'json',
});

let errorCount: number = 0;

// ── Token helpers ──
function isTokenExpired(token: string): boolean {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    // Refresh 60 seconds before actual expiry
    return Date.now() >= (payload.exp * 1000) - 60000;
  } catch {
    return true;
  }
}

let isRefreshing = false;
let refreshPromise: Promise<string | null> | null = null;
let refreshSubscribers: ((token: string | null) => void)[] = [];

async function refreshToken(): Promise<string> {
  const token = localStorage.getItem('token');
  const response = await axios.post(
    `${process.env.REACT_APP_API_URL}/auth/refresh-token`,
    {},
    { headers: { Authorization: `Bearer ${token}` } },
  );
  const newToken = response.data.accessToken;
  localStorage.setItem('token', newToken);
  return newToken;
}

// ── Background token refresh ──
// Proactively refresh the token every 15 minutes so idle tabs stay logged in.
// QuestionPro access tokens expire in 20 minutes (1200s).
const BACKGROUND_REFRESH_INTERVAL = 15 * 60 * 1000; // 15 minutes
let backgroundRefreshTimer: ReturnType<typeof setInterval> | null = null;

function startBackgroundRefresh() {
  if (backgroundRefreshTimer) return;
  backgroundRefreshTimer = setInterval(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      stopBackgroundRefresh();
      return;
    }
    // Skip dev tokens
    if (process.env.REACT_APP_DEV_TOKEN && token === process.env.REACT_APP_DEV_TOKEN) return;
    // Only refresh if token is near expiry (within 5 minutes)
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      const timeLeft = (payload.exp * 1000) - Date.now();
      if (timeLeft < 5 * 60 * 1000) {
        refreshToken().catch(() => {
          // Refresh failed — user will be prompted to log in on next interaction
        });
      }
    } catch {
      // Can't decode token — skip
    }
  }, BACKGROUND_REFRESH_INTERVAL);
}

function stopBackgroundRefresh() {
  if (backgroundRefreshTimer) {
    clearInterval(backgroundRefreshTimer);
    backgroundRefreshTimer = null;
  }
}

// Start background refresh if a token exists
if (localStorage.getItem('token')) {
  startBackgroundRefresh();
}

// Listen for login/logout across tabs
window.addEventListener('storage', (e) => {
  if (e.key === 'token') {
    if (e.newValue) {
      startBackgroundRefresh();
    } else {
      stopBackgroundRefresh();
    }
  }
});

async function ensureFreshToken(): Promise<string | null> {
  const token = localStorage.getItem('token');
  if (!token) return null;

  // Dev bypass tokens are not JWTs — never try to refresh them
  if (process.env.REACT_APP_DEV_TOKEN && token === process.env.REACT_APP_DEV_TOKEN) {
    return token;
  }

  if (!isTokenExpired(token)) return token;

  // Token expired — refresh it (only one refresh at a time)
  if (!isRefreshing) {
    isRefreshing = true;
    refreshPromise = refreshToken()
      .then((newToken) => {
        startBackgroundRefresh();
        return newToken;
      })
      .catch(() => {
        stopBackgroundRefresh();
        const _appOrigin = localStorage.getItem('appOrigin');
        localStorage.clear();
        if (_appOrigin) localStorage.setItem('appOrigin', _appOrigin);
        window.location.href = '/';
        return null;
      })
      .finally(() => {
        isRefreshing = false;
        refreshPromise = null;
      });
  }

  return refreshPromise;
}

// ── Request interceptor: check expiry and attach token ──
apiClient.interceptors.request.use(async config => {
  const loginAs = localStorage.getItem('loginAs');

  const token = await ensureFreshToken();
  if (token) {
    config.headers.common.Authorization = `Bearer ${token}`;
    config.headers.ts = moment();
  }

  if (loginAs) {
    const { id } = JSON.parse(loginAs);
    config.headers.common.loginAs = id;
  }

  const activeWorkspaceId = localStorage.getItem('activeWorkspaceId');
  if (activeWorkspaceId) {
    config.headers['x-workspace-id'] = activeWorkspaceId;
  }

  return config;
});

// ── Response interceptor ──
apiClient.interceptors.response.use(
  (response: AxiosResponse) => {
    if (errorCount !== 0) {
      errorCount = 0;
    }

    // If the backend refreshed the token (guard detected near-expiry), pick up the new one.
    const newToken = response.headers?.['x-new-access-token'];
    if (newToken) {
      localStorage.setItem('token', newToken);
    }

    // Performance monitor logging
    setTimeout(() => {
      const monitor: string | null = localStorage.getItem('monitor');
      const data: any[] = monitor ? JSON.parse(monitor) : [];

      const startTime = response?.config?.headers['ts'];
      const _baseUrl = response.config.baseURL?.slice(0, -1);

      if (startTime) {
        const saveRes = {
          id: Math.floor(100000 + Math.random() * 900000),
          status: response.status,
          url: !response.config.url?.startsWith('https') ? `${_baseUrl}${response.config.url}` : response.config.url,
          method: response.config.method,
          contentLength: response.headers?.['content-length'],
          startTime,
          duration: moment.duration(startTime.diff(moment())).asMilliseconds(),
        };

        data.push(saveRes);
        localStorage.setItem('monitor', JSON.stringify(data));
      }
    });

    return response;
  },
  async error => {
    const originalRequest = error?.config;
    const status = error?.response?.status;

    // Performance monitor logging for errors
    setTimeout(() => {
      const monitor: string | null = localStorage.getItem('monitor');
      const data: any[] = monitor ? JSON.parse(monitor) : [];

      const startTime = error?.response?.config?.headers['ts'];

      if (startTime) {
        const saveRes = {
          id: Math.floor(100000 + Math.random() * 900000),
          status: error.response.status,
          url: !error.config.url?.startsWith('https') ? `${error.config.baseURL}${error.config.url}` : error.config.url,
          method: error.response.config.method,
          startTime,
          duration: moment.duration(startTime.diff(moment())).asMilliseconds(),
        };

        data.push(saveRes);
        localStorage.setItem('monitor', JSON.stringify(data));
      }
    });

    // Handle 401: refresh token and retry (queue concurrent 401s)
    if (status === 401 && originalRequest && !originalRequest._retry) {
      originalRequest._retry = true;

      if (isRefreshing) {
        // Another 401 is already refreshing — wait for it
        return new Promise((resolve, reject) => {
          refreshSubscribers.push((newToken: string | null) => {
            if (newToken) {
              originalRequest.headers.Authorization = `Bearer ${newToken}`;
              resolve(apiClient(originalRequest));
            } else {
              reject(error);
            }
          });
        });
      }

      isRefreshing = true;
      try {
        const newToken = await refreshToken();
        startBackgroundRefresh();
        // Notify all queued requests with the new token
        refreshSubscribers.forEach(cb => cb(newToken));
        refreshSubscribers = [];
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        return apiClient(originalRequest);
      } catch {
        // Notify all queued requests that refresh failed
        refreshSubscribers.forEach(cb => cb(null));
        refreshSubscribers = [];
        stopBackgroundRefresh();
        const _appOrigin = localStorage.getItem('appOrigin');
        localStorage.clear();
        if (_appOrigin) localStorage.setItem('appOrigin', _appOrigin);
        window.location.href = '/';
        return Promise.reject(error);
      } finally {
        isRefreshing = false;
      }
    }

    if (errorCount && error.response) {
      console.log({
        message: `${error?.response?.status}`,
        description: `${error?.response?.statusText}`,
        style: { borderColor: 'red' },
        duration: 3,
      });
    }

    const { response } = error;
    if (response) {
      return Promise.reject(response.data);
    }
    return Promise.reject(error);
  },
);

export default apiClient;
